import {types} from "mobx-state-tree";
import { reaction } from "mobx";
import Notification from "../../../../utils/Notification";
import { MODULE_WIDTH_CONSTRAINTS, SIZE_CONSTRAINTS, BWR_CONSTRAINTS, DECIMAL_LIMITS } from '../../../../utils/constants';
import { convertUnitValue, exceedsDecimalLimit, validForBWRImageConfig } from '../../../../utils/common';


const ImageAndCharacteristicsState = types.model('ImageAndCharacteristicsState', {
  imageFormat: types.optional(types.string, 'png'),
  isCMYK: types.optional(types.boolean, false),
  size: types.optional(types.number, 1.0),
  isValidSize: types.optional(types.boolean, true),
  moduleWidth: types.optional(types.number, 1.0),
  showImageSizeOrModuleWidth: types.optional(types.boolean, true), // true = image size, false = module width
  unitsOfMeasure: types.optional(types.string, 'inch'),
  resolution: types.optional(types.integer, 1200),
  isValidResolution: types.optional(types.boolean, true),
  errorCorrection: types.optional(types.string, 'M'),
  quietZone: types.optional(types.integer, 4),
  bwr: types.optional(types.number, 0),
}).volatile(self => ({
  notifyDesigner: types.f,
  logoListener: types.f,
})).actions((self) => ({
  afterCreate() {
  },
  setConsumer(f) {
    self.notifyDesigner = f
  },
  handleFormatSelect(e) {
    self.imageFormat = e.target.value
    if (self.notifyDesigner) {
      self.notifyDesigner("imageFormat",e.target.value)
    }
  },
  handleUnitOfMeasure(e) {
    const oldUnit = self.unitsOfMeasure;
    const newUnit = e.target.value;
    self.unitsOfMeasure = newUnit;
    if (self.notifyDesigner) {
      self.notifyDesigner("unitsOfMeasure", newUnit);
    }

    // Convert size
    if (self.size !== 0) {
      const sizeLimits = SIZE_CONSTRAINTS[newUnit] || SIZE_CONSTRAINTS.inch;
      const sizeDecimals = newUnit === 'mm' ? 1 : 2;
      const newSize = +Math.min(Math.max(convertUnitValue(self.size, oldUnit, newUnit, sizeDecimals), sizeLimits.min), sizeLimits.max).toFixed(sizeDecimals);
      self.size = newSize;
      if (self.notifyDesigner) self.notifyDesigner("size", newSize);
    }

    // Convert moduleWidth
    if (self.moduleWidth !== 0) {
      const mwLimits = MODULE_WIDTH_CONSTRAINTS[newUnit] || MODULE_WIDTH_CONSTRAINTS.inch;
      const mwDecimals = newUnit === 'mm' ? 2 : 3;
      const newMW = +Math.min(Math.max(convertUnitValue(self.moduleWidth, oldUnit, newUnit, mwDecimals), mwLimits.min), mwLimits.max).toFixed(mwDecimals);
      self.moduleWidth = newMW;
      if (self.notifyDesigner) self.notifyDesigner("moduleWidth", newMW);
    }

    // Convert BWR (0 = no BWR, skip)
    if (self.bwr !== 0) {
      const bwrLimits = BWR_CONSTRAINTS[newUnit] || BWR_CONSTRAINTS.inch;
      const bwrDecimals = newUnit === 'mm' ? 3 : 4;
      const newBWR = +Math.min(Math.max(convertUnitValue(self.bwr, oldUnit, newUnit, bwrDecimals), bwrLimits.min), bwrLimits.max).toFixed(bwrDecimals);
      self.bwr = newBWR;
      if (self.notifyDesigner) self.notifyDesigner("bwr", newBWR);
    }
  },
  handleIntegerChange(e) {
    if (e.target.value >= 0) {
      self[e.target.id] = +e.target.value
      if (self.notifyDesigner) {
        self.notifyDesigner(e.target.id,+e.target.value)
      }
    }
  },
  handleSizeChange(e){
    if (e.target.value === '') { self.size = -1; return; }
    if (exceedsDecimalLimit(e.target.value, self.unitsOfMeasure, DECIMAL_LIMITS.size)) return;
    // Accept any intermediate value while typing; validation happens on blur
    const value = +e.target.value;
    self.size = value;
    const limits = SIZE_CONSTRAINTS[self.unitsOfMeasure] || SIZE_CONSTRAINTS.inch;
    if (value >= limits.min && value <= limits.max && self.notifyDesigner) {
      self.notifyDesigner("size", value);
    }
  },
  handleResolutionChange(e){
    if (e.target.value === '') { self.resolution = 0; return; }
    const value = +e.target.value
    if (value > 1200) {
      self.isValidResolution = false
      return
    }
    self.isValidResolution = true
    self.resolution = value
    if (self.notifyDesigner) {
      self.notifyDesigner("resolution",value)
    }
  },
  //We don't support BWR yet but here is the code when we do
  // handleBwr(e) {
  //   self.bwr = e.target.value
  // },
  handleErrorCorrection(e) {
    self.errorCorrection = e.target.value
    if (self.notifyDesigner) {
      self.notifyDesigner("errorCorrection",e.target.value)
    }
  },
  consumeLogoCorrection(key,value){
    self[key] = value
  },
  handleQuiteZone(e) {
    self.quietZone = +e.target.value
    if (self.notifyDesigner) {
      self.notifyDesigner("quietZone",+e.target.value)
    }
  },
  changeQuietZone(value){
    self.quietZone = value
    if (self.notifyDesigner) {
      self.notifyDesigner("quietZone",value)
    }
  },
  handleBwrChange(e) {
    const rawValue = e.target.value;

    // Block typing past the allowed decimal limit
    if (exceedsDecimalLimit(rawValue, self.unitsOfMeasure, DECIMAL_LIMITS.bwr)) return;

    // Accept any intermediate value while typing; validation happens on blur
    const value = rawValue === '' ? 0 : Number(rawValue);
    self.bwr = value;
    if (self.notifyDesigner) {
      self.notifyDesigner("bwr", value);
    }
  },
  handleSizeBlur() {
    const limits = SIZE_CONSTRAINTS[self.unitsOfMeasure] || SIZE_CONSTRAINTS.inch;
    const outOfRange = self.size >= 0 && (self.size < limits.min || self.size > limits.max);
    if (self.size < 0 || outOfRange) {
      const defaultSizeByUnit = { inch: 1, cm: 2.54, mm: 25.4 };
      const resetValue = outOfRange ? limits.min : (defaultSizeByUnit[self.unitsOfMeasure] || 1);
      self.size = resetValue;
      self.isValidSize = true;
      if (self.notifyDesigner) {
        self.notifyDesigner("size", resetValue);
      }
      if (outOfRange) {
        new Notification()
          .setType("error")
          .setMessage(`Size must be between ${limits.min} and ${limits.max} ${self.unitsOfMeasure}`)
          .send();
      }
    }
  },
  handleModuleWidthBlur() {
    const limits = MODULE_WIDTH_CONSTRAINTS[self.unitsOfMeasure] || MODULE_WIDTH_CONSTRAINTS.inch;
    const outOfRange = self.moduleWidth > 0 && (self.moduleWidth < limits.min || self.moduleWidth > limits.max);
    if (self.moduleWidth < 0 || outOfRange) {
      const resetValue = limits.min;
      self.moduleWidth = resetValue;
      if (self.notifyDesigner) {
        self.notifyDesigner("moduleWidth", resetValue);
      }
      if (outOfRange) {
        new Notification()
          .setType("error")
          .setMessage(`Module width must be between ${limits.min} and ${limits.max} ${self.unitsOfMeasure}`)
          .send();
      }
    }
  },
  handleResolutionBlur() {
    if (self.resolution === 0) {
      self.isValidResolution = true;
      self.resolution = 1200;
      if (self.notifyDesigner) {
        self.notifyDesigner("resolution", 1200);
      }
    }
  },
  handleBwrBlur() {
    const limits = BWR_CONSTRAINTS[self.unitsOfMeasure] || BWR_CONSTRAINTS.inch;
    if (self.bwr !== 0 && (self.bwr < limits.min || self.bwr > limits.max)) {
      new Notification()
        .setType("error")
        .setMessage(`Please enter a valid width reduction 0 or (${limits.min} - ${limits.max} ${self.unitsOfMeasure})`)
        .send();
      self.bwr = 0;
      if (self.notifyDesigner) {
        self.notifyDesigner("bwr", 0);
      }
    }
  },
  handleModuleWidthChange(e) {
    if (e.target.value !== '' && exceedsDecimalLimit(e.target.value, self.unitsOfMeasure, DECIMAL_LIMITS.moduleWidth)) return;
    // Accept any intermediate value while typing; validation happens on blur
    const value = e.target.value === '' ? -1 : +e.target.value;
    self.moduleWidth = value;
    if (value !== 0 && self.notifyDesigner) {
      self.notifyDesigner("moduleWidth", value);
    }
  },
  toggleSizeDisplay() {
    self.showImageSizeOrModuleWidth = !self.showImageSizeOrModuleWidth
    if (self.notifyDesigner) {
      self.notifyDesigner("showImageSizeOrModuleWidth", self.showImageSizeOrModuleWidth)
    }
    // Reset to min value when switching modes
    self.setDefaultSizeModuleWidth();
  },
  setDefaultSizeModuleWidth() {
    if (self.showImageSizeOrModuleWidth) {
      const defaultSizeByUnit = { inch: 1, cm: 2.54, mm: 25.4 };
      const defaultSize = defaultSizeByUnit[self.unitsOfMeasure] || 1;
      self.size = defaultSize
      if (self.notifyDesigner) {
        self.notifyDesigner("size", defaultSize)
      }
    } else {
      const minValue = MODULE_WIDTH_CONSTRAINTS[self.unitsOfMeasure]?.min || MODULE_WIDTH_CONSTRAINTS.inch.min
      self.moduleWidth = minValue
      if (self.notifyDesigner) {
        self.notifyDesigner("moduleWidth", minValue)
      }
    }
  },
  setDefaultBWRSize() {
    if (self.bwr === 0) return;
    const minBWR = BWR_CONSTRAINTS[self.unitsOfMeasure]?.min || BWR_CONSTRAINTS.inch.min
    self.bwr = minBWR
    if (self.notifyDesigner) {
      self.notifyDesigner("bwr", minBWR)
    }
  },
  enforceImageSizeMode() {
    if (!self.showImageSizeOrModuleWidth) {
      self.showImageSizeOrModuleWidth = true
      self.size = 1
      if (self.notifyDesigner) {
        self.notifyDesigner("showImageSizeOrModuleWidth", true)
        self.notifyDesigner("size", 1)
      }
    }
  },
  forceInvalidateBwr() {
    if (self.bwr !== 0) {
      self.bwr = 0;
      if (self.notifyDesigner) {
        self.notifyDesigner("bwr", 0);
      }
    }
  }

}))

export default ImageAndCharacteristicsState;
